'use client';

import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { createClient } from '@/lib/supabase/client';

export type Review = {
  id: string;
  user_id: string;
  restaurant_stt: number;
  rating: number;
  comment: string | null;
  image_url: string | null;
  created_at: string;
  user_name?: string;
};

export function useReviews(restaurantStt: number) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    const supabase = createClient();
    if (!supabase) { setLoading(false); return; }

    const { data } = await supabase
      .from('reviews')
      .select('*')
      .eq('restaurant_stt', restaurantStt)
      .order('created_at', { ascending: false });

    setReviews(data || []);
    setLoading(false);
  }, [restaurantStt]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  const addReview = useCallback(async (rating: number, comment: string, imageUrl?: string) => {
    const supabase = createClient();
    if (!supabase) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from('reviews').insert({
      user_id: user.id,
      restaurant_stt: restaurantStt,
      rating,
      comment: comment ? DOMPurify.sanitize(comment, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) : null,
      image_url: imageUrl || null,
    });
    if (!error) fetchReviews();
  }, [restaurantStt, fetchReviews]);

  const deleteReview = useCallback(async (reviewId: string) => {
    const supabase = createClient();
    if (!supabase) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase.from('reviews').delete().eq('id', reviewId).eq('user_id', user.id);
    if (!error) fetchReviews();
  }, [fetchReviews]);

  return { reviews, avgRating, reviewCount: reviews.length, addReview, deleteReview, loading };
}

/** Batch fetch avg ratings for all restaurants */
export function useAvgRatings() {
  const [ratings, setRatings] = useState<Record<number, { avg: number; count: number }>>({});

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) return;

    supabase.from('reviews').select('restaurant_stt, rating').then(({ data }) => {
      if (!data) return;
      const grouped: Record<number, number[]> = {};
      for (const r of data) {
        if (!grouped[r.restaurant_stt]) grouped[r.restaurant_stt] = [];
        grouped[r.restaurant_stt].push(r.rating);
      }
      const result: Record<number, { avg: number; count: number }> = {};
      for (const [stt, ratings] of Object.entries(grouped)) {
        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        result[Number(stt)] = { avg: Math.round(avg * 10) / 10, count: ratings.length };
      }
      setRatings(result);
    });
  }, []);

  return ratings;
}
